import { BrokerListQuery, BrokerType, ClientMyBroker, MAX_BROKER_LIST_LIMIT } from '@numeo/types-core'
import { brokersApi } from 'api/brokers.api'
import { useEffect, useSyncExternalStore } from 'react'
import { log } from 'utils/logger'
import { currentBrokerCacheOwner as currentOwner } from './brokerCacheOwner'

const CLASSIFIED_TYPES = [BrokerType.IN_BLACKLIST, BrokerType.FAVORITE]
// Favorite, blacklisted and red-flagged brokers are a small slice of a company's brokers; cap the read all the same.
const MAX_PAGES = 5
// After a failed load, mounting consumers don't retry until this long has passed.
const RETRY_FAILED_AFTER_MS = 60_000

let brokers: ClientMyBroker[] = []
let loaded = false
let loadingOwner: string | null = null
let cacheOwner = ''
let failure: { owner: string; at: number } | null = null
const listeners = new Set<() => void>()

let reloadQueued = false

let version = 0
function notify(): void {
    version++
    listeners.forEach((listener) => listener())
}

async function loadAll(query: BrokerListQuery): Promise<ClientMyBroker[]> {
    const all: ClientMyBroker[] = []
    let total = 0
    for (let page = 1; page <= MAX_PAGES; page++) {
        const { items, pagination } = await brokersApi.listBrokers({ ...query, page, limit: MAX_BROKER_LIST_LIMIT })
        all.push(...items)
        total = pagination.total
        if (all.length >= total || items.length === 0) break
    }
    if (all.length < total) {
        log.w('[useClassifiedBrokers] broker cap reached; matching by email or name may miss brokers', { query, loaded: all.length, total })
    }
    return all
}

async function load(): Promise<void> {
    const owner = currentOwner()
    if (loadingOwner === owner) return
    loadingOwner = owner
    try {
        const [classified, redFlagged] = await Promise.all([loadAll({ type: CLASSIFIED_TYPES }), loadAll({ redFlagged: true })])
        if (owner !== currentOwner()) return
        brokers = [...new Map([...classified, ...redFlagged].map((broker) => [broker.id, broker])).values()]
        cacheOwner = owner
        loaded = true
        failure = null
        notify()
    } catch (error) {
        log.e('[useClassifiedBrokers] failed to load favorite, blacklisted and red-flagged brokers', error)
        failure = { owner, at: Date.now() }
        notify()
    } finally {
        if (loadingOwner === owner) loadingOwner = null
        if (reloadQueued) {
            reloadQueued = false
            void load()
        }
    }
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

interface ClassifiedSnapshot {
    brokers: ClientMyBroker[]
    loading: boolean
}

const NO_BROKERS: ClientMyBroker[] = []
let cachedSnapshot: { key: string; value: ClassifiedSnapshot } | null = null

/**
 * `loading` lives in the snapshot so a failure (which leaves the broker list unchanged) still re-renders consumers.
 * Both fields are scoped to the signed-in account: another account's data or failure never counts as this one's.
 */
function getSnapshot(): ClassifiedSnapshot {
    const owner = currentOwner()
    const key = `${version}:${owner}`
    if (cachedSnapshot?.key !== key) {
        const ready = cacheOwner === owner
        // After a failure consumers stop waiting and treat every broker as unclassified.
        cachedSnapshot = { key, value: { brokers: ready ? brokers : NO_BROKERS, loading: !ready && failure?.owner !== owner } }
    }
    return cachedSnapshot.value
}

const recentlyFailed = (): boolean => !!failure && failure.owner === currentOwner() && Date.now() - failure.at < RETRY_FAILED_AFTER_MS

/** Reload after a favorite / blacklist change. */
export function refetchClassifiedBrokers(): void {
    if (loadingOwner) reloadQueued = true
    else if (loaded) void load()
}

/**
 * The company's favorite, blacklisted and red-flagged brokers. For matching by email, domain or name when
 * there's no MC; anything keyed by MC should use `useMyBrokers(mcNumbers)` instead.
 */
export const useClassifiedBrokers = () => {
    useEffect(() => {
        if ((!loaded || cacheOwner !== currentOwner()) && !recentlyFailed()) void load()
    }, [])
    return useSyncExternalStore(subscribe, getSnapshot)
}
